import { Feature, FeatureCollection } from 'geojson';
import { nanoid } from "nanoid";
import React, {
  createContext,
  useCallback,
  useEffect,
  useState
} from "react";

import { LatLngTuple } from 'leaflet';
import { getDataSources } from '../api/data';
import { getMesh, getMeshData } from '../api/maps';
import { getAllValidations as getAllValidationsSuperAdmin } from '../api/super-admin';
import { getAllValidations } from '../api/validations';
import { DataSourceModel, MapLocation, MeshResponseData, MeshResponseDataRaw, ModelTypes, ProviderTypes, Validation } from '../core/global';
import { retrieveItem, storeItem } from '../core/storage';
import { LayerDescription, ModelShape } from '../data/layers';
import { calculateCellResolution, convertDataSourcesResponseToModelShape, convertResponseToGeoJson, getItemByKeyValue, getLatLonFromString, isNumber } from '../helpers/core';
import useIsSuperAdmin from '../hooks/useIsSuperAdmin';
import useLoggedIn from '../hooks/useLoggedIn';

export const detaultMapCenter: LatLngTuple = [54.43155619, 8.66226817]

const intialFeatureLocationData = {
  lat: null, 
  lon: null, 
  cenLat: null, 
  cenLon: null, 
  sqm: null
}
interface FeatureLocation {
  lat: null | number
  lon: null | number
  cenLat: null | number
  cenLon: null | number
  sqm: null | number
}

interface ExtremeValuesProps {
  min: number | null
  max: number | null
}

export interface ManualScaleValuesProps {
  min: number | string | null
  max: number | string | null
  show_bands?: boolean | null
  reverse_colors?: boolean | null
}

interface updateMeshDataProps {
  coords?: string | null, 
  useModel?: DataSourceModel | undefined | null, 
  showAverages?: boolean | null | undefined
  lat?: number | null
  lon?: number | null
  filename?: string | null
  orderId?: string | null
}

interface MapDataContextProps {
  zoom: number
  setZoom: React.Dispatch<React.SetStateAction<number>>
  hasModelGrid: boolean
  dataSources: ModelShape | null
  currentLayer: LayerDescription | null
  refetchDataSources: () => void
  setCurrentLayer: React.Dispatch<React.SetStateAction<LayerDescription | null>>;
  dataSourceModel: DataSourceModel | null,
  setDataSourceModel: (val: DataSourceModel | null) => void
  meshData: MeshResponseData[]
  meshDataGeoJson: FeatureCollection | null
  updateMeshData: (props: updateMeshDataProps) => void
  meshDataKey: string
  setMeshDataGeoJson: (val: FeatureCollection | null) => void
  currentFeature: FeatureCollection | null
  setCurrentFeature: React.Dispatch<React.SetStateAction<FeatureCollection | null>>
  currentMapProvider: ProviderTypes | null 
  setCurrentMapProvider: React.Dispatch<React.SetStateAction<ProviderTypes | null>>
  showMax: boolean
  setShowMax: React.Dispatch<React.SetStateAction<boolean>>
  showMin: boolean
  setShowMin: React.Dispatch<React.SetStateAction<boolean>>
  mapCenter: MapLocation | undefined | null
  setMapCenter: React.Dispatch<React.SetStateAction<MapLocation | undefined | null>>
  currentMapCenter: MapLocation | undefined | null
  setCurrentMapCenter: React.Dispatch<React.SetStateAction<MapLocation | undefined | null>>
  extremeFeatureMax: Feature[]
  setExtremeFeatureMax: React.Dispatch<React.SetStateAction<Feature[]>>
  extremeFeatureMin: Feature[]
  setExtremeFeatureMin: React.Dispatch<React.SetStateAction<Feature[]>>
  currentPropertyKey: string | null
  setCurrentPropertyKey: React.Dispatch<React.SetStateAction<string | null>>
  getFeatureLocation: () => FeatureLocation
  showValidations: boolean
  setShowValidations: React.Dispatch<React.SetStateAction<boolean>>
  showValidationsSuperAdmin: boolean
  setShowValidationsSuperAdmin: React.Dispatch<React.SetStateAction<boolean>>
  privateValidationsSuperAdmin: Validation[]
  showDynamicScaling: boolean
  setShowDynamicScaling: React.Dispatch<React.SetStateAction<boolean>>
  validations: Validation[]
  privateValidations: Validation[]
  fetchPrivateValidations: () => Promise<void>
  setExtremeFeatureMaxValue: React.Dispatch<React.SetStateAction<string | number | null>>
  extremeFeatureMaxValue: string | number | null
  setExtremeFeatureMinValue: React.Dispatch<React.SetStateAction<string | number | null>>
  extremeFeatureMinValue: string | number | null
  showModelGrid: boolean
  setShowModelGrid: React.Dispatch<React.SetStateAction<boolean>>
  hasAverages: boolean
  showAverages: boolean
  setShowAverages: React.Dispatch<React.SetStateAction<boolean>>
  // extremes: ExtremeValuesProps,
  scaleValues: ManualScaleValuesProps | null,
  // setExtremes: React.Dispatch<React.SetStateAction<ExtremeValuesProps>>
  setScaleValues: React.Dispatch<React.SetStateAction<ManualScaleValuesProps | null>>
  loadingValidations: boolean,
  currentSelectionParams: {
    [key: string]: any
  } | null,
  setCurrentSelectionParams: React.Dispatch<React.SetStateAction<{[key: string]: any} | null>>
  gridPoints: any[]
  setGridPoints: React.Dispatch<React.SetStateAction<any[]>>
  activePointId: string | null
  setActivePointId: React.Dispatch<React.SetStateAction<string | null>>
}

const initialValue: MapDataContextProps = {
  gridPoints: [],
  setGridPoints: () => null,
  activePointId: null,
  setActivePointId: () => null,
  setCurrentSelectionParams: () => null,
  currentSelectionParams: null,
  zoom: 6,
  setZoom: () => {},
  hasModelGrid: false,
  dataSources: null,
  setCurrentLayer: () => {},
  refetchDataSources: () => {},
  currentLayer: null,
  dataSourceModel: null,
  meshData: [],
  meshDataGeoJson: null,
  updateMeshData: () => null,
  setDataSourceModel: () => null,
  meshDataKey: nanoid(),
  setMeshDataGeoJson: () => null,
  currentFeature: null,
  setCurrentFeature: () => null,
  currentMapProvider: 'arcgisonline',
  setCurrentMapProvider: () => null,
  showMax: false,
  setShowMax: () => null,
  showMin: true,
  setShowMin: () => null,
  mapCenter: null,
  setMapCenter: () => null,
  currentMapCenter: {
    location: {
      lat: detaultMapCenter[0],
      lng: detaultMapCenter[1],
    }
  },
  setCurrentMapCenter: () => null,
  extremeFeatureMax: [],
  setExtremeFeatureMax: () => null,
  extremeFeatureMin: [],
  setExtremeFeatureMin: () => null,
  currentPropertyKey: null,
  setCurrentPropertyKey: () => null,
  getFeatureLocation: () => intialFeatureLocationData,
  showValidations: true,
  setShowValidations: () => null,
  showValidationsSuperAdmin: false,
  setShowValidationsSuperAdmin: () => null,
  privateValidationsSuperAdmin: [],
  showDynamicScaling: false,
  setShowDynamicScaling: () => {},
  validations: [],
  privateValidations: [],
  fetchPrivateValidations: async () => undefined,
  setExtremeFeatureMaxValue: () => null,
  extremeFeatureMaxValue: null,
  setExtremeFeatureMinValue: () => null,
  extremeFeatureMinValue: null,
  showModelGrid: false,
  setShowModelGrid: () => null,
  hasAverages: false,
  showAverages: false,
  setShowAverages: () => null,
  // extremes: {
  //   min: null,
  //   max: null
  // },
  scaleValues: null,
  // setExtremes: () => null,
  setScaleValues: () => null,
  loadingValidations: false,
};

export const MapDataContext = createContext<MapDataContextProps>(initialValue);

interface MapDataProviderProps {
  children?: React.ReactNode;
}

export const MapDataProvider = ({
  children
}: MapDataProviderProps) => {
  const [gridPoints, setGridPoints] = useState<any[]>(initialValue.gridPoints)
  const [activePointId, setActivePointId] = useState<string | null>(initialValue.activePointId)
  const [currentSelectionParams, setCurrentSelectionParams] = useState(initialValue.currentSelectionParams)
  const [zoom, setZoom] = useState<number>(initialValue.zoom)
  // const [extremes, setExtremes] = useState<{min: number | null, max: number | null}>(initialValue.extremes)
  const [scaleValues, setScaleValues] = useState(initialValue.scaleValues)
  const [hasModelGrid, setHasModelGrid] = useState(initialValue.hasModelGrid)
  const [dataSources, setDataSources] = useState<ModelShape | null>(null)
  const [loadingValidations, setLoadingValidations] = useState(initialValue.loadingValidations)
  const [currentLayer, setCurrentLayer] = useState(initialValue.currentLayer)
  const [showValidations, setShowValidations] = useState(initialValue.showValidations)
  const [showValidationsSuperAdmin, setShowValidationsSuperAdmin] = useState(initialValue.showValidations)
  const [privateValidationsSuperAdmin, setPrivateValidationsSuperAdmin] = useState(initialValue.privateValidationsSuperAdmin) 
  const [showDynamicScaling, setShowDynamicScaling] = useState(initialValue.showDynamicScaling)
  const [validations, setValidations] = useState(initialValue.validations)
  const [privateValidations, setPrivateValidations] = useState(initialValue.privateValidations)
  const [currentPropertyKey, setCurrentPropertyKey] = useState<string | null>(null)
  const [extremeFeatureMin, setExtremeFeatureMin] = useState<Feature[]>([])
  const [extremeFeatureMax, setExtremeFeatureMax] = useState<Feature[]>([])
  const [extremeFeatureMinValue, setExtremeFeatureMinValue] = useState(initialValue.extremeFeatureMinValue)
  const [extremeFeatureMaxValue, setExtremeFeatureMaxValue] = useState(initialValue.extremeFeatureMaxValue)

  const [mapCenter, setMapCenter] = useState(initialValue.mapCenter)
  const [currentMapCenter, setCurrentMapCenter] = useState(initialValue.currentMapCenter)
  const [showMax, setShowMax] = useState(initialValue.showMax)
  const [showMin, setShowMin] = useState(initialValue.showMin)
  const [currentMapProvider, setCurrentMapProvider] = useState(initialValue.currentMapProvider)
  const [currentFeature, setCurrentFeature] = React.useState(initialValue.currentFeature);
  const [meshDataKey, setMeshDataKey] = useState(initialValue.meshDataKey)
  const [meshData, setMeshData] = useState(initialValue.meshData)
  const [meshDataGeoJson, setMeshDataGeoJson] = useState<FeatureCollection | null>(initialValue.meshDataGeoJson)
  const [dataSourceModel, setDataSourceModel] = useState(initialValue.dataSourceModel)
  const [showModelGrid, setShowModelGrid] = useState(initialValue.showModelGrid)
  const [showAverages, setShowAverages] = useState(initialValue.showAverages)
  const [hasAverages, setHasAverages] = useState(initialValue.hasAverages)

  const getDataSourcesForUser = useCallback(async () => {
    const result = await getDataSources()
    const {response} = result
    if (response && Array.isArray(response)) {
      const convertedShape = convertDataSourcesResponseToModelShape(response) || {}
      setDataSources(convertedShape)
      const savedLastLocation: any = retrieveItem('map-location') || {}
      const {key, provider, model, geography, version, lat, lon} = savedLastLocation

      // HACK: disable auto load of last location
      const disable = true
      if (isNumber(lat) && isNumber(lon) && !disable) {
        setMapCenter({
          location: {
            lat: Number(lat),
            lng: Number(lon)
          },
        })
        if (key && provider && model && geography) {
          
          updateMeshData({
              lat,
              lon,
              useModel: {
                key,
                provider,
                model,
                geography,
                version,
              } as DataSourceModel
            })
        } else {
          updateMeshData({lat, lon})
        }
      }

      const keyExists = !!(convertedShape && key && Object.prototype.hasOwnProperty.call(convertedShape, key))
      if (keyExists && provider && model && geography) {
        setDataSourceModel({
          key,
          provider,
          model,
          geography,
          version,
        } as DataSourceModel)
      } else {
        const keys = Object.keys(convertedShape)
        if (keys.length > 0) {
          // get default data source Model
          const defaultDataSourceModel = convertedShape[keys[0]]
          const {
            provider,
            model,
            geography,
            version,
          } = defaultDataSourceModel
          setDataSourceModel({
            key: `${provider}-${model}-${geography}-${version}`,
            provider,
            model,
            geography,
            version,
          } as DataSourceModel)
        }
      }
    }

  }, [])

  useEffect(() => {
    getDataSourcesForUser()
  }, [])

  useEffect(() => {
    if (!dataSourceModel) {
      return
    }

    const execute = async (_dataSource: DataSourceModel | null) => {
      if (_dataSource) {
        setLoadingValidations(true)
        const result = await getAllValidations({
          ..._dataSource
        })
        setLoadingValidations(false)
        const {response} = result
        if (response) {
          if (Array.isArray(response)) {
            setValidations(response as Validation[])
          }
        }
      } 
    }
    execute(dataSourceModel)

    const savedLastLocation: any = retrieveItem('map-location') || {}
    storeItem('map-location', {
      ...savedLastLocation,
      ...dataSourceModel,
    })

  }, [JSON.stringify(dataSourceModel)])

  const isLoggedIn = useLoggedIn()

  const fetchPrivateValidations = useCallback(async () => {
    if (!dataSourceModel) {
      return
    }

    if (!isLoggedIn) {
      setPrivateValidations(initialValue.privateValidations)
      return
    }
    setLoadingValidations(true)
    const result = await getAllValidations({
      ...dataSourceModel,
      privateOnly: true,
    })
    const {response} = result
    setLoadingValidations(false)
    if (response) {
      if (Array.isArray(response) && response.length > 0) {
        setPrivateValidations(response as Validation[])
      } else {
        setPrivateValidations([])
      }
    }
  }, [dataSourceModel, isLoggedIn])

  useEffect(() => {
    const execute = async (_dataSource: DataSourceModel | null) => {
      await fetchPrivateValidations()
    }
    execute(dataSourceModel)
  }, [dataSourceModel, isLoggedIn])

  const isSuperAdmin = useIsSuperAdmin()


  useEffect(() => {
    const execute = async () => {
      const result = await getAllValidationsSuperAdmin(true)
      const {response: {results}} = result
      if (results) {
        if (Array.isArray(results) && results.length > 0) {
          setPrivateValidationsSuperAdmin(results as Validation[])
        } else {
          setPrivateValidationsSuperAdmin(initialValue.privateValidations)
        }
      }
    }
    if (isSuperAdmin && isLoggedIn) {
      execute()
    } else {
      setPrivateValidationsSuperAdmin(initialValue.privateValidations)
    }
  }, [isSuperAdmin, isLoggedIn])

  useEffect(() => {
    // find first layer of the new model
    if (dataSourceModel && dataSources && Object.prototype.hasOwnProperty.call(dataSources, dataSourceModel.key)) {
      const modelObject = dataSources[dataSourceModel.key]
      const layers: LayerDescription[] = modelObject.layers
      const foundGrid = layers.find(i => i.valueKey === 'model-grid')
      setHasModelGrid(!!foundGrid)
      setHasAverages(modelObject.has_averages || false)
      if (layers && Array.isArray(layers) && layers.length > 0) {
        const [_currentLayer] = layers
        if (_currentLayer) {
          // set default layer to the first 
          setCurrentLayer(_currentLayer)
        }
      }
    }
  }, [JSON.stringify(dataSourceModel), JSON.stringify(dataSources)])

  useEffect(() => {
    if (currentPropertyKey !== null 
      && dataSources 
      && dataSourceModel 
      && Object.prototype.hasOwnProperty.call(dataSources, dataSourceModel.key)
    ) {
      const allLayers = [...Object.keys(dataSources[dataSourceModel.key])]
      const found = getItemByKeyValue(allLayers, 'valueKey', currentPropertyKey)
      if (found !== -1) {
        setCurrentLayer(found)
      } else {
        setCurrentLayer(null)
      }
    }
  }, [currentPropertyKey, JSON.stringify(dataSources), JSON.stringify(dataSourceModel)])

  const getFeatureLocation = useCallback(() => {
    if (meshDataGeoJson 
      && meshDataGeoJson.features 
      && Array.isArray(meshDataGeoJson.features)
      && dataSourceModel
      && meshDataGeoJson.features.length > 0) {
        const {properties} = meshDataGeoJson.features[0]
        // @ts-ignore
        const { coords, centroid, sqm } = properties;
        const {lat, lon} = getLatLonFromString(coords)
        const {lat: cenLat, lon: cenLon} = getLatLonFromString(centroid)
        const useSqm = calculateCellResolution(sqm, dataSourceModel.model as ModelTypes)
        return {
          lat, 
          lon, 
          cenLat, 
          cenLon, 
          sqm: useSqm
        }
      }
    return intialFeatureLocationData
  }, [JSON.stringify(meshDataGeoJson), dataSourceModel, showAverages, hasAverages])

  interface ResultProps {
    response: any,
    coords?: string,
    provider?: string | null,
    model?: string | null,
    geography?: string | null,
    version?: string | null,
  }

  const setResult = useCallback(({
    response, 
    coords = '', 
    provider = null, 
    model = null, 
    geography = null, 
    version = null
  }: ResultProps) => {
    const setEmpty = () => {
      const [lat, lon] = coords.split(',')
      if (lat !== null || lat !== undefined || lon !== null || lon !== undefined) {
        return
      }
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(lat), Number(lon)],
        },
        properties: {
          centroid: coords.replace(/[,]/, ' '),
          coords: coords.replace(/[,]/, ' '),
          provider, 
          model, 
          geography,
          version,
          sqm: null,
        },
      };
      setMeshData([])
      setMeshDataKey(nanoid())
      setMeshDataGeoJson({
        "type": "FeatureCollection",
        "features": [feature]
      })
    }
    if (response && Array.isArray(response)) {
      const rows = [...response]
      if (rows.length > 0) {
        const converted: Feature[] = convertResponseToGeoJson(rows as MeshResponseDataRaw[])
        setMeshData(rows as MeshResponseData[])
        setMeshDataKey(nanoid())
        setMeshDataGeoJson({
          "type": "FeatureCollection",
          "features": converted
        })
      } else {
        setEmpty()
      }
    } else {
      setEmpty()
    }
  }, [])

  const updateMeshData = useCallback(async ({
    coords = null,
    lat = null, 
    lon = null, 
    filename = null,
    orderId = null,
    showAverages: useShowAverages = null,
    useModel = null
  }: updateMeshDataProps) => {

    const hasCoords = !!(typeof coords === "string" && coords !== '')
    const hasLatLon = !!(lat !== null && lon !== null)
    if (coords === null && filename === null && orderId === null) {
      setMeshDataKey(initialValue.meshDataKey)
      setMeshData(initialValue.meshData)
      setMeshDataGeoJson(initialValue.meshDataGeoJson)
    } else if (
      (hasCoords || hasLatLon)
       && (useModel || dataSourceModel)) 
    {
      const {provider, model, geography, version} = useModel || dataSourceModel || {} as DataSourceModel
      let averages: boolean | null | undefined = null

      if (model === 'era5') {
        averages = useShowAverages !== null ? useShowAverages : hasAverages ? showAverages : null
      }
      let useCoord = ''
      if (hasCoords) {
        useCoord = coords
      } else if (hasLatLon) {
        useCoord = `${lat},${lon}`
      }

      const result = await getMesh({
        coords: useCoord,
        model,
        provider,
        geography,
        version, 
        averages,
      })
      const {response} = result
      setResult({response, coords: useCoord, provider, model, geography, version})
    } else if (filename && orderId ) {
      const result = await getMeshData({filename, order_id: orderId})
      const {response} = result
      setResult({response})
    }
  }, [JSON.stringify(dataSourceModel), showAverages, hasAverages])
  
  return (
    <MapDataContext.Provider
      value={{
        hasModelGrid,
        dataSources,
        dataSourceModel,
        refetchDataSources: getDataSourcesForUser,
        setDataSourceModel,
        meshData,
        meshDataGeoJson,
        updateMeshData,
        meshDataKey,
        setMeshDataGeoJson,
        currentFeature, 
        setCurrentFeature,
        currentMapProvider, 
        setCurrentMapProvider,
        showMax, 
        setShowMax,
        showMin, 
        setShowMin,
        mapCenter, 
        setMapCenter,
        currentMapCenter,
        setCurrentMapCenter,
        extremeFeatureMax,
        extremeFeatureMin,
        setExtremeFeatureMax,
        setExtremeFeatureMin,
        currentPropertyKey,
        setCurrentPropertyKey,
        getFeatureLocation,
        showValidations, 
        setShowValidations,
        showValidationsSuperAdmin,
        setShowValidationsSuperAdmin,
        privateValidationsSuperAdmin,
        showDynamicScaling: false, 
        setShowDynamicScaling,
        validations,
        privateValidations,
        fetchPrivateValidations,
        extremeFeatureMaxValue,
        setExtremeFeatureMaxValue,
        extremeFeatureMinValue,
        setExtremeFeatureMinValue,
        setCurrentLayer,
        currentLayer,
        showModelGrid,
        setShowModelGrid,
        // extremes,
        // setExtremes,
        zoom,
        setZoom,
        scaleValues, 
        setScaleValues,
        showAverages, 
        setShowAverages,
        hasAverages,
        loadingValidations,
        currentSelectionParams,
        setCurrentSelectionParams,
        gridPoints,
        setGridPoints,
        activePointId,
        setActivePointId,
        
      }}
    >
      {children}
    </MapDataContext.Provider>
  );
}